/**
 * Everything the header numbers, the goal rings and the alert list are made of.
 * All pure functions over the loaded rows — no I/O, so it is cheap to recompute
 * on every keystroke and easy to reason about.
 */
import {
  addDays,
  diffDays,
  diffMonths,
  endOfMonth,
  monthOf,
  startOfMonth,
  today as todayISO,
} from './dates'
import { occurrences } from './recurrence'
import { alarming, budgetTone } from './budgets'
import { goalPlan, setAsideOn } from './savings'
import { round2 } from './money'

// Nothing in the ledger is a saving any more; money out is money out.
const isSpend = (t) => t.amount < 0

/** What the account itself holds: opening balance plus everything up to today. */
export function currentBalance(transactions, opening = 0, today = todayISO()) {
  return round2(
    transactions.reduce((sum, t) => (t.date <= today ? sum + t.amount : sum), Number(opening) || 0),
  )
}

/**
 * What you may think you have: the account, less everything the goals have
 * quietly set aside by that day. Saving never moves money, so this is the only
 * place it shows up.
 */
export function spendableBalance(transactions, opening, goals, today = todayISO()) {
  return round2(currentBalance(transactions, opening, today) - setAsideOn(goals, today))
}

/**
 * The opening balance implied by the balance the bank shows today — the
 * inverse of `currentBalance`, for a ledger that starts mid-account.
 */
export function openingFromBalance(transactions, balance, today = todayISO()) {
  return round2(Number(balance) - currentBalance(transactions, 0, today))
}

/**
 * End-of-day balance for every day in the window — the balance chart's series.
 * Transactions before `from` are carried in, so the line starts at the balance
 * as it stood that morning rather than at zero.
 *
 * Savings step the line down like anything else: pass the goals' projected rows
 * in among the transactions and the line dips on the day each instalment is
 * taken, which is the day the ledger shows it.
 */
export function balanceSeries(transactions, opening = 0, from, to) {
  const perDay = new Map()
  let running = Number(opening) || 0
  for (const t of transactions) {
    if (t.date < from) running += t.amount
    else if (t.date <= to) perDay.set(t.date, (perDay.get(t.date) ?? 0) + t.amount)
  }
  const out = []
  for (let day = from; day <= to; day = addDays(day, 1)) {
    running += perDay.get(day) ?? 0
    out.push({ date: day, value: round2(running) })
  }
  return out
}

/**
 * Money in, out and put by per day in the window — the day-scoped trend chart.
 *
 * Three buckets rather than two: a saving leaves the balance like spending
 * does, but it is not spending, and a month that looks expensive only because
 * a goal took its instalment would be a lie. Sign decides the other two, which
 * is how the ledger's own totals count — the charts follow whatever the list is
 * showing, so the two must agree.
 */
const emptyBucket = () => ({ income: 0, spent: 0, saved: 0 })

const intoBucket = (row, t) => {
  if (t.saving) row.saved -= t.amount
  else if (t.amount > 0) row.income += t.amount
  else row.spent -= t.amount
}

const roundBucket = (row) => ({
  income: round2(row.income),
  spent: round2(row.spent),
  saved: round2(row.saved),
})

export function dailySeries(transactions, from, to) {
  const buckets = new Map()
  for (const t of transactions) {
    if (t.date < from || t.date > to) continue
    const row = buckets.get(t.date) ?? emptyBucket()
    intoBucket(row, t)
    buckets.set(t.date, row)
  }
  const out = []
  for (let day = from; day <= to; day = addDays(day, 1)) {
    out.push({ date: day, ...roundBucket(buckets.get(day) ?? emptyBucket()) })
  }
  return out
}

/**
 * The last point of each month, for a line read at month resolution. The month
 * in progress keeps whatever its latest point is — a month-end balance that has
 * not happened yet would be a projection.
 */
export function monthEnds(points) {
  const out = []
  for (const point of points) {
    const previous = out[out.length - 1]
    if (previous && monthOf(previous.date) === monthOf(point.date)) out[out.length - 1] = point
    else out.push(point)
  }
  return out
}

/** The same, per month, oldest first. */
export function monthlySeries(transactions, months) {
  const first = `${months[0] ?? ''}-01`
  const last = endOfMonth(months[months.length - 1] ?? '')
  const buckets = new Map(months.map((month) => [month, emptyBucket()]))
  for (const t of transactions) {
    if (t.date < first || t.date > last) continue
    const row = buckets.get(monthOf(t.date))
    if (!row) continue
    intoBucket(row, t)
  }
  return months.map((month) => ({ month, ...roundBucket(buckets.get(month)) }))
}

export function monthTotals(transactions, month) {
  const rows = transactions.filter((t) => monthOf(t.date) === month)
  const income = rows.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0)
  const spent = rows.filter(isSpend).reduce((s, t) => s - t.amount, 0)
  return {
    income: round2(income),
    spent: round2(spent),
    net: round2(income - spent),
    count: rows.length,
  }
}

/** What a goal has set aside so far, and what it is committed to next. */
export function goalProgress(goal, today = todayISO()) {
  const plan = goalPlan(goal, today)
  const monthly = monthlyRate(plan)
  return {
    ...plan,
    saved: plan.setAside,
    // The panel and the cards speak in months whatever the cadence.
    perMonth: round2(monthly),
    thisMonth: round2(instalmentsIn(plan, monthOf(today))),
    monthsLeft: plan.next ? Math.max(0, Math.round(diffMonths(today, plan.end ?? today))) : 0,
    dueDate: plan.end,
    dueThisMonth: round2(instalmentsIn(plan, monthOf(today), today)),
  }
}

/** A cadence-agnostic monthly figure, for anything that talks in months. */
function monthlyRate(plan) {
  const perYear = { weekly: 52, fortnightly: 26, monthly: 12, quarterly: 4 }[plan.cadence] ?? 12
  return (plan.perInstalment * perYear) / 12
}

/** What a goal commits inside one month — optionally only the part still ahead. */
function instalmentsIn(plan, month, after = null) {
  return plan.schedule
    .filter((i) => monthOf(i.date) === month && (after === null || i.date > after))
    .reduce((sum, i) => sum + i.amount, 0)
}

/**
 * "Daily allowance" — what is genuinely free to spend per day for the rest of
 * the period, once every commitment still ahead has been set aside.
 *
 * balance + income still expected
 *         − planned expenses not yet seen (including overdue ones)
 *         − savings still owed this month
 *
 * There is no cushion of its own here. Money you want kept back is money you
 * are saving, and a goal already says so — it comes out of the balance before
 * this runs (`spendableBalance`), which is one mechanism instead of two.
 */
export function computeAllowance({
  balance,
  schedule,
  goalStates,
  transactions,
  settings,
  today = todayISO(),
}) {
  const month = monthOf(today)
  const periodEnd = endOfMonth(month)
  const daysLeft = Math.max(1, diffDays(today, periodEnd) + 1)

  const openRows = schedule.filter((r) => !r.matches.length && r.due <= periodEnd)
  const upcomingIncome = settings.countUpcomingIncome
    ? round2(openRows.filter((r) => r.type === 'income').reduce((s, r) => s + r.expected, 0))
    : 0
  const committed = round2(
    openRows.filter((r) => r.type === 'expense').reduce((s, r) => s + r.expected, 0),
  )
  // Whatever the goals have already committed is out of the balance; what is
  // left of this month still has to be held back.
  const savingsDue = round2(goalStates.reduce((s, g) => s + g.dueThisMonth, 0))

  const safeToSpend = round2(balance + upcomingIncome - committed - savingsDue)
  const perDay = round2(Math.max(0, safeToSpend) / daysLeft)

  const spentToday = round2(
    transactions.filter((t) => t.date === today && isSpend(t)).reduce((s, t) => s - t.amount, 0),
  )
  // Whatever no planned occurrence claimed. Nothing is written on the
  // transaction, so the schedule is what knows.
  const spokenFor = new Set(schedule.flatMap((row) => row.matches.map((t) => t.id)))
  const discretionaryThisMonth = round2(
    transactions
      .filter((t) => monthOf(t.date) === month && isSpend(t) && !spokenFor.has(t.id))
      .reduce((s, t) => s - t.amount, 0),
  )

  return {
    safeToSpend,
    perDay,
    daysLeft,
    periodEnd,
    upcomingIncome,
    committed,
    savingsDue,
    spentToday,
    todayLeft: round2(perDay - spentToday),
    discretionaryThisMonth,
    projectedEnd: round2(balance + upcomingIncome - committed - savingsDue),
  }
}

/** Planned versus actual income for a month, once the due dates have passed. */
export function incomeCheck(schedule, month, today = todayISO()) {
  const rows = schedule.filter((r) => r.type === 'income' && monthOf(r.due) === month)
  const elapsed = rows.filter((r) => r.due <= today || r.matches.length)
  const planned = round2(elapsed.reduce((s, r) => s + r.expected, 0))
  const actual = round2(elapsed.reduce((s, r) => s + r.actual, 0))
  return {
    planned,
    actual,
    variance: round2(actual - planned),
    rows,
    pending: rows.filter((r) => !r.matches.length && r.due > today),
  }
}

/**
 * The "something is not according to plan" feed, most urgent first.
 */
export function buildAlerts({
  schedule,
  budgets = [],
  goalStates,
  allowance,
  income,
  month,
  fmt,
  // Injected rather than imported: this file is pure, and the language lives in
  // the store. Defaults to the key, which is enough for a test.
  t = (key) => key,
  today = todayISO(),
}) {
  const alerts = []
  const currentMonth = month === monthOf(today)

  // A budget only speaks when it is off the way it was set up to warn about:
  // a spending limit passed, or an income target the period closed short of.
  // Coming in under a limit is the point of having one and says nothing.
  for (const row of budgets) {
    if (!alarming(row)) continue
    const diff = Math.abs(row.variance)
    alerts.push({
      id: `budget:${row.id}`,
      // The same three bands the row is coloured in: past the figure is worth
      // seeing, past the padding is the alarm that was asked for.
      tone: budgetTone(row),
      title: t(
        row.type === 'income'
          ? 'alert.budgetShort'
          : row.status === 'limit'
            ? 'alert.budgetOverLimit'
            : 'alert.budgetOver',
        { category: row.name ?? row.categories?.join(', ') ?? '', amount: fmt(diff) },
      ),
      detail: t(row.running ? 'alert.budgetDetailRunning' : 'alert.budgetDetailDone', {
        actual: fmt(row.actual),
        expected: fmt(row.expected),
      }),
      budgetId: row.budgetId,
      due: row.to,
    })
  }

  for (const row of schedule) {
    if (monthOf(row.due) !== month) continue
    if (row.status === 'missing') {
      const late = diffDays(row.due, today)
      alerts.push({
        id: `missing:${row.id}`,
        tone: row.type === 'income' ? 'danger' : late > 3 ? 'danger' : 'warn',
        title: t(row.type === 'income' ? 'alert.notArrived' : 'alert.notShownUp', {
          category: row.plan.category,
        }),
        detail: t('alert.missingDetail', {
          amount: fmt(row.expected),
          when: late === 0 ? t('alert.dueToday') : t('alert.dueDaysAgo', { count: late }),
        }),
        planId: row.planId,
        category: row.plan.category,
        due: row.due,
      })
    } else if (row.status === 'over' || row.status === 'under') {
      // Income drift is reported once, in aggregate, further down — a line per
      // payslip on top of that would just be the same news twice.
      if (row.type === 'income') continue
      const diff = Math.abs(row.variance)
      const worse = row.type === 'income' ? row.status === 'under' : row.status === 'over'
      alerts.push({
        id: `variance:${row.id}`,
        tone: worse ? 'warn' : 'good',
        title: t(row.variance > 0 ? 'alert.cameInHigher' : 'alert.cameInLower', {
          category: row.plan.category,
          amount: fmt(diff),
        }),
        detail: t('alert.varianceDetail', {
          expected: fmt(row.expected),
          actual: fmt(row.actual),
        }),
        planId: row.planId,
        category: row.plan.category,
        due: row.due,
      })
    }
  }

  if (currentMonth && income.planned > 0 && income.variance < -0.5) {
    alerts.push({
      id: 'income:short',
      tone: 'danger',
      title: t('alert.incomeShort', { amount: fmt(Math.abs(income.variance)) }),
      detail: t('alert.incomeDetail', { actual: fmt(income.actual), planned: fmt(income.planned) }),
    })
  } else if (currentMonth && income.variance > 0.5) {
    alerts.push({
      id: 'income:over',
      tone: 'good',
      title: t('alert.incomeOver', { amount: fmt(income.variance) }),
      detail: t('alert.incomeDetail', { actual: fmt(income.actual), planned: fmt(income.planned) }),
    })
  }


  if (currentMonth) {
    if (allowance.projectedEnd < 0) {
      alerts.push({
        id: 'balance:negative',
        tone: 'danger',
        title: t('alert.monthShort', { amount: fmt(Math.abs(allowance.projectedEnd)) }),
        detail: t('alert.monthShortDetail'),
      })
    }
    if (allowance.safeToSpend <= 0 && allowance.projectedEnd >= 0) {
      alerts.push({
        id: 'allowance:zero',
        tone: 'warn',
        title: t('alert.noneLeft'),
        detail: t('alert.noneLeftDetail'),
      })
    } else if (allowance.todayLeft < 0) {
      alerts.push({
        id: 'allowance:today',
        tone: 'warn',
        title: t('alert.overToday', { amount: fmt(Math.abs(allowance.todayLeft)) }),
        detail: t('alert.overTodayDetail', {
          spent: fmt(allowance.spentToday),
          perDay: fmt(allowance.perDay),
        }),
      })
    }
  }

  const rank = { danger: 0, warn: 1, info: 2, good: 3 }
  return alerts.sort((a, b) => rank[a.tone] - rank[b.tone])
}

/**
 * What the plans actually add up to inside a window, counted occurrence by
 * occurrence rather than averaged: a weekly bill lands four or five times
 * depending on the month, a quarterly one only in the months it is due, and a
 * one-off only if it falls there at all.
 *
 * @returns {{income: number, expense: number, items: number}}
 */
export function plannedBetween(plans, from, to) {
  let income = 0
  let expense = 0
  let items = 0
  for (const plan of plans) {
    const due = occurrences(plan, from, to) // skips archived plans itself
    if (!due.length) continue
    items += due.length
    const total = Math.abs(plan.amount) * due.length
    if (plan.type === 'income') income += total
    else expense += total
  }
  return { income: round2(income), expense: round2(expense), items }
}

export { startOfMonth, endOfMonth }
